import React, { useCallback, useContext, useEffect, useState } from 'react'
import { Breadcrumbs } from '@/components/ui/breadcrumb'
import { Dialog } from '@/components/ui/dialog'
import { SortDescriptor } from '@progress/kendo-data-query'
import {
  Grid,
  GridCellProps,
  GridColumn,
  GridColumnResizeEvent,
  GridSortChangeEvent,
} from '@progress/kendo-react-grid'
import { CreateReferralMaterial } from 'api/ClientArea/CreateReferralMaterial'
import { ReferralMaterialsDto } from 'api/ClientArea/ReferralMaterialsDto'
import { CreateFilter } from 'api/Identity/Types/CreateFilter'
import { PageData } from 'api/Pagination/Types'
import AddButton from 'components/AddButton/AddButton'
import { Pager } from 'components/components/Pager'
import ConfirmationModal from 'components/ConfirmationModal/ConfirmationModal'
import { FilterType } from 'components/Filter'
import { SelectFiltersFacade } from 'components/Filter/ClientFields/SelectFiltersFacade'
import MoreButton from 'components/MoreButton/MoreButton'
import { Spinner } from 'components/Spinner'
import { successToast, ToastContext } from 'components/Toast/Toast'
import { createGridState, useGridFilter, useGridState } from 'features/DataGrid'
import { useGridStatusState } from 'features/DataGrid/hooks/useGridStatusState'
import { isGoBack } from 'lib/react-router-dom'
import { useAppDispatch, useAppSelector } from 'store'
import {
  fetchPromoMaterialsOptionsAction,
  fetchReferralMaterialsCategoriesByEntity,
  fetchReferralMaterialsPromoContentTypes,
  fetchReferralMaterialsPromoSizes,
  fetchReferralMaterialsTypes,
} from 'store/clientarea/clientarea.actions'
import { fetchClientLanguagesAction } from 'store/clients/clients.actions'
import {
  CompositeFilterDescriptor,
  GridFilterActionState,
  resetGridFilters,
  selectGridFilters,
  setGridFilters,
} from 'store/grid'
import {
  fetchFilterAction,
  fetchFilterTypesAction,
  fetchTickmillCompaniesAction,
  removeFilterAction,
  saveFilterAction,
  updateFilterAction,
} from 'store/identity/identity.actions'
import { useApiClient } from 'utils/api/ApiClient'
import { ClientAreaApiClient } from 'utils/api/clientAreaApi'
import { ClientsApiClient } from 'utils/api/clientsApi'
import { IdentityApiClient } from 'utils/api/identityApi'
import { links } from 'utils/featureToggles'
import { isEmpty } from 'utils/is-empty'
import { buildQuery, buildSortQuery } from 'utils/QueryHelpers'
import { TableStateContext, TableStateContextData } from 'utils/TableStateContext'

import { GridColumn as GridColumnProps } from '../campaignsColumn'
import { GridResizeContext, referralMaterialsColumn } from './referralMaterialsColumns'
import { getReferralMaterialsPageFilterFields } from './referralMaterialsFilters'
import { ReferralMaterialsForm } from './referralMaterialsForm'

const useMapStateToProps = () => ({
  languages: useAppSelector((state) => state.clients.language),
  tickmillCompanies: useAppSelector((state) => state.identity.tickmillCompanies),
  promoMaterialsOptions: useAppSelector((state) => state.clientArea.promoMaterialsOptions),
  referralMaterialsTypes: useAppSelector((state) => state.clientArea.referralMaterialsTypes),
  referralMaterialsPromoSizes: useAppSelector(
    (state) => state.clientArea.referralMaterialsPromoSizes
  ),
  referralMaterialsPromoContentTypes: useAppSelector(
    (state) => state.clientArea.referralMaterialsPromoContentTypes
  ),
  referralMaterialsCategoriesByEntity: useAppSelector(
    (state) => state.clientArea.referralMaterialsCategoriesByEntity
  ),
  filters: useAppSelector((state) => state.identity.filter),
  filterTypes: useAppSelector((state) => state.identity.filterType),
  gridState: useAppSelector((state) => state.grid),
  hasFilters: useAppSelector(selectGridFilters),
})

const useMapDispatchToProps = () => {
  const dispatch = useAppDispatch()
  const clientsApiClient = useApiClient(ClientsApiClient)
  const identityApiClient = useApiClient(IdentityApiClient)
  const clientAreaApiClient = useApiClient(ClientAreaApiClient)

  const fetchReferralMaterialsCategoriesByEntityCallback = useCallback(
    async (tickmillCompanyId: number | string, isCategoryActive: boolean) =>
      dispatch(
        fetchReferralMaterialsCategoriesByEntity(
          clientAreaApiClient,
          tickmillCompanyId,
          isCategoryActive
        )
      ),
    [clientAreaApiClient, dispatch]
  )

  return {
    fetchFilterTypes: async () => dispatch(fetchFilterTypesAction(identityApiClient)),
    fetchFilters: async () => dispatch(fetchFilterAction(identityApiClient)),
    saveFilter: async (data: CreateFilter) => dispatch(saveFilterAction(data, identityApiClient)),
    updateFilter: async (filterId: string, data: CreateFilter) =>
      dispatch(updateFilterAction(filterId, data, identityApiClient)),
    fetchTickmillCompanies: async () => dispatch(fetchTickmillCompaniesAction(identityApiClient)),
    removeFilter: async (id: string) => dispatch(removeFilterAction(id, identityApiClient)),
    setGridFilters: async (state: GridFilterActionState) => dispatch(setGridFilters(state)),
    resetGridFilters: async () => dispatch(resetGridFilters()),
    fetchClientLanguages: async () => dispatch(fetchClientLanguagesAction(clientsApiClient)),
    fetchPromoMaterialsOptions: async () =>
      dispatch(fetchPromoMaterialsOptionsAction(clientAreaApiClient)),
    fetchReferralMaterialsTypes: async () =>
      dispatch(fetchReferralMaterialsTypes(clientAreaApiClient)),
    fetchReferralMaterialsPromoSizes: async () =>
      dispatch(fetchReferralMaterialsPromoSizes(clientAreaApiClient)),
    fetchReferralMaterialsPromoContentTypes: async () =>
      dispatch(fetchReferralMaterialsPromoContentTypes(clientAreaApiClient)),
    fetchReferralMaterialsCategoriesByEntity: fetchReferralMaterialsCategoriesByEntityCallback,
  }
}

const useReferralMaterialsFetch = () => {
  const clientAreaApiClient = useApiClient(ClientAreaApiClient)

  const [tableData, setTableData] = useState<TableStateContextData>({ skipCount: true })
  const [referralMaterials, setReferralMaterials] = useState<ReferralMaterialsDto[]>([])

  const { gridStatusState, gridStatusActions } = useGridStatusState()
  const { gridState, gridActions } = useGridState()
  const { selectFilterForGoBackHistory } = useGridFilter(gridStatusActions)
  const {
    languages,
    tickmillCompanies,
    filters,
    filterTypes,
    promoMaterialsOptions,
    referralMaterialsTypes,
    referralMaterialsPromoSizes,
    referralMaterialsPromoContentTypes,
    referralMaterialsCategoriesByEntity,
  } = useMapStateToProps()
  const {
    fetchFilterTypes,
    fetchFilters,
    fetchClientLanguages,
    fetchTickmillCompanies,
    fetchPromoMaterialsOptions,
    fetchReferralMaterialsTypes,
    fetchReferralMaterialsPromoSizes,
    fetchReferralMaterialsPromoContentTypes,
    fetchReferralMaterialsCategoriesByEntity,
  } = useMapDispatchToProps()

  useEffect(() => {
    fetchReferralMaterialsFetch()

    if (isEmpty(filterTypes)) {
      fetchFilterTypes()
    }
    if (isEmpty(filters)) {
      fetchFilters()
    }

    if (isEmpty(languages)) {
      fetchClientLanguages()
    }

    if (isEmpty(tickmillCompanies)) {
      fetchTickmillCompanies()
    }
    if (isEmpty(promoMaterialsOptions?.baseUrls)) {
      fetchPromoMaterialsOptions()
    }
    if (isEmpty(referralMaterialsTypes)) {
      fetchReferralMaterialsTypes()
    }
    if (isEmpty(referralMaterialsPromoSizes)) {
      fetchReferralMaterialsPromoSizes()
    }
    if (isEmpty(referralMaterialsPromoContentTypes)) {
      fetchReferralMaterialsPromoContentTypes()
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const fetchReferralMaterialsFetch = async () => {
    await fetchGrid(
      async (gridFilterState: GridFilterActionState): Promise<PageData<ReferralMaterialsDto[]>> => {
        const searchQuery = getQuery(gridFilterState.filter)
        const sortQuery = getSortQuery(gridFilterState.sort)
        const response = await clientAreaApiClient.getReferralMaterials({
          search: searchQuery,
          sort: sortQuery,
          pageIndex: gridFilterState.pageIndex,
          pageSize: gridFilterState.pageSize,
          skipCount: tableData?.skipCount !== false,
        })

        if (response?.items) {
          setReferralMaterials(response.items)
        }
        setTableData({ ...tableData, meta: response })

        return response
      }
    )
  }

  const fetchGrid = async (
    callback: (gridFilterState: GridFilterActionState) => Promise<PageData<ReferralMaterialsDto[]>>
  ) => {
    gridStatusActions.setLoading(true)

    const gridFilterState = selectFilterForGoBackHistory(
      createGridState({ skip: 0 }, gridState, { isGoBack })
    )

    const response = await callback(gridFilterState)
    if (response?.totalResults >= 0) {
      const total = response.totalResults

      gridActions.setGridFilters({
        total,
      })
    }

    gridStatusActions.setLoading(false)
  }

  const getQuery = useCallback(
    (filter?: CompositeFilterDescriptor) => {
      return buildQuery(filter ?? gridState.filter) ?? null
    },
    [gridState.filter]
  )

  const getSortQuery = useCallback(
    (sortDescriptors?: SortDescriptor[]): string | null => {
      return buildSortQuery(sortDescriptors ?? gridState.sort)
    },
    [gridState.sort]
  )

  const filterFetch = useCallback(
    async (params?: { query?: string | null }): Promise<void> => {
      const searchQuery = params?.query !== undefined ? params.query : getQuery()
      const sortQuery = getSortQuery(gridState.sort)
      const pageIndex = 1
      const pageSize = gridState.pageSize ?? 10
      const take = gridState.take ?? 10
      const skip = 0

      const response = await clientAreaApiClient.getReferralMaterials({
        search: searchQuery,
        sort: sortQuery,
        pageIndex,
        pageSize,
        skipCount: tableData?.skipCount !== false,
      })
      const total = response.totalResults

      if (response?.items) {
        setReferralMaterials(response.items.map((x) => Object.assign({ selected: false }, x)))
      }
      gridActions.setGridFilters({
        take,
        skip,
        total,
        pageIndex,
      })
      setTableData({ ...tableData, meta: response })
    },
    // tslint:disable-next-line:max-line-length
    [
      getQuery,
      getSortQuery,
      gridActions,
      gridState.pageSize,
      gridState.sort,
      gridState.take,
      tableData?.skipCount,
    ] // eslint-disable-line react-hooks/exhaustive-deps
  )

  return {
    fetchReferralMaterialsFetch,
    fetchReferralMaterialsCategoriesByEntity,
    promoMaterialsOptions,
    referralMaterialsTypes,
    referralMaterialsPromoSizes,
    referralMaterialsPromoContentTypes,
    referralMaterialsCategoriesByEntity,
    languages,
    tickmillCompanies,
    gridState,
    gridActions,
    filters,
    filterTypes,
    filterFetch,
    tableData,
    setTableData,
    referralMaterials,
    setReferralMaterials,
    setGridFilters: gridActions.setGridFilters,
    gridStatusState,
    gridStatusActions,
    getQuery,
    getSortQuery,
  }
}

const useReferralMaterialsMutate = () => {
  const clientAreaApiClient = useApiClient(ClientAreaApiClient)
  const setToast = useContext(ToastContext)

  const handleAddSubmit = async (data: CreateReferralMaterial) => {
    await clientAreaApiClient.createReferralMaterial(data)
    setToast(successToast(`Referral Material added successfully.`))
  }

  const handleRemove = async (data: ReferralMaterialsDto) => {
    await clientAreaApiClient.deleteReferralMaterial(data.id)
    setToast(successToast(`Referral Material removed successfully.`))
  }

  return { handleAddSubmit, handleRemove }
}

const useReferralMaterialsHandlers = (props: ReturnType<typeof useReferralMaterialsFetch>) => {
  const { gridState, gridActions } = props
  const { getQuery, getSortQuery, setReferralMaterials, tableData, setTableData } = props

  const clientAreaApiClient = useApiClient(ClientAreaApiClient)

  const handleSkipCountChange = async (skipCount: boolean) => {
    props.gridStatusActions.setFetching(true)
    const searchQuery = getQuery()
    const sortQuery = getSortQuery(gridState.sort)

    const response = await clientAreaApiClient.getReferralMaterials({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.take,
      skipCount,
    })
    const total = response.totalResults

    props.gridStatusActions.setFetching(false)

    if (response?.items) {
      setReferralMaterials(response.items)
    }
    gridActions.setGridFilters({
      total,
    })
    setTableData({ ...tableData, skipCount, meta: response })
  }

  const handleSortChange = async (event: GridSortChangeEvent) => {
    gridActions.setGridFilters({
      sort: event.sort,
    })

    props.gridStatusActions.setFetching(true)
    const searchQuery = getQuery(gridState.filter)
    const sortQuery = getSortQuery(event.sort)
    const response = await clientAreaApiClient.getReferralMaterials({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: gridState.pageIndex,
      pageSize: gridState.pageSize,
      skipCount: tableData?.skipCount !== false,
    })

    props.gridStatusActions.setFetching(false)

    if (response?.items) {
      setReferralMaterials(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handlePageSizeChange = async (pageSize: number) => {
    props.gridStatusActions.setFetching(true)
    await gridActions.setGridFilters({
      ...gridState,
      pageSize,
    })

    const searchQuery = getQuery()
    const sortQuery = getSortQuery(gridState.sort)

    const response = await clientAreaApiClient.getReferralMaterials({
      search: searchQuery,
      sort: sortQuery,
      pageIndex: 1,
      pageSize,
      skipCount: tableData?.skipCount !== false,
    })

    props.gridStatusActions.setFetching(false)
    await gridActions.setGridFilters({
      ...gridState,
      pageIndex: 1,
      take: pageSize,
      total: response.totalResults,
    })
    if (response?.items) {
      setReferralMaterials(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  const handleGridPageChange = async (page: number) => {
    props.gridStatusActions.setFetching(true)
    const searchQuery = getQuery()
    const sortQuery = getSortQuery(gridState.sort)
    const pageIndex = page
    gridActions.setGridFilters({
      pageIndex,
    })

    const response = await clientAreaApiClient.getReferralMaterials({
      search: searchQuery,
      sort: sortQuery,
      pageIndex,
      pageSize: gridState.take,
      skipCount: tableData?.skipCount !== false,
    })
    const total = response.totalResults

    props.gridStatusActions.setFetching(false)

    gridActions.setGridFilters({
      total,
    })

    gridActions.setGridFilters({
      total,
    })
    if (response?.items) {
      setReferralMaterials(response.items)
    }
    setTableData({ ...tableData, meta: response })
  }

  return {
    handleSkipCountChange,
    handleSortChange,
    handlePageSizeChange,
    handleGridPageChange,
    filterColumn,
  }
}

interface ModalProps {
  data: ReferralMaterialsDto | undefined
  visible: boolean
}

const initialModalState = { data: undefined, visible: false }

const useReferralMaterialModal = () => {
  const [referralMaterialModal, setReferralMaterialModal] = useState<ModalProps>(initialModalState)

  const handleReferralMaterialModalOpen = (data?: ReferralMaterialsDto) => {
    setReferralMaterialModal({ data, visible: true })
  }

  const handleReferralMaterialModalClose = () => {
    setReferralMaterialModal(initialModalState)
  }

  return {
    referralMaterialModal,
    handleReferralMaterialModalOpen,
    handleReferralMaterialModalClose,
  }
}

const useConfirmationModal = () => {
  const [confirmationModal, setConfirmationModal] = useState<ModalProps>(initialModalState)

  const handleConfirmationModalOpen = (data?: ReferralMaterialsDto) => {
    if (data) {
      setConfirmationModal({ data, visible: true })
    }
  }

  const handleConfirmationModalClose = () => {
    setConfirmationModal(initialModalState)
  }

  return { confirmationModal, handleConfirmationModalOpen, handleConfirmationModalClose }
}

const ReferralMaterialsPage = () => {
  const crumbs = [{ title: 'Marketing - Referral Materials', link: links.referralMaterials.url }]
  const [columnResizeCount, setColumnResizeCount] = useState(0)

  const handleColumnResizeTracking = (event: GridColumnResizeEvent) => {
    if (event.end) {
      setColumnResizeCount((prev) => prev + 1)
    }
  }

  const referralMaterialsFetch = useReferralMaterialsFetch()
  const {
    gridState,
    gridStatusState,
    languages,
    tickmillCompanies,
    referralMaterialsTypes,
    referralMaterialsCategoriesByEntity,
    fetchReferralMaterialsCategoriesByEntity,
    gridStatusActions,
    gridActions,
    filterFetch,
    getQuery,
  } = referralMaterialsFetch
  const { tableData, setTableData, referralMaterials } = referralMaterialsFetch

  const {
    referralMaterialModal,
    handleReferralMaterialModalOpen,
    handleReferralMaterialModalClose,
  } = useReferralMaterialModal()
  const { confirmationModal, handleConfirmationModalOpen, handleConfirmationModalClose } =
    useConfirmationModal()

  const handlers = useReferralMaterialsHandlers(referralMaterialsFetch)
  const mutate = useReferralMaterialsMutate()
  const { handleSortChange, handleGridPageChange, handlePageSizeChange, handleSkipCountChange } =
    handlers

  const handleSubmit = async (data: CreateReferralMaterial) => {
    await mutate.handleAddSubmit(data)
    await referralMaterialsFetch.fetchReferralMaterialsFetch()
    handleReferralMaterialModalClose()
  }

  const handleRemoveReferralMaterials = async (data?: ReferralMaterialsDto) => {
    if (data?.id) {
      await mutate.handleRemove(data)
      await referralMaterialsFetch.fetchReferralMaterialsFetch()
      handleConfirmationModalClose()
    }
  }

  if (gridStatusState.isLoading) {
    return <Spinner />
  }

  return (
    <React.Fragment>
      {confirmationModal.data && (
        <ConfirmationModal
          yesButtonText='Proceed'
          onYes={() => handleRemoveReferralMaterials(confirmationModal.data)}
          onCancel={handleConfirmationModalClose}
          visible={confirmationModal.visible}
          renderTitle={
            <div>
              <p>Are you sure you want to delete this IB referral material page record?</p>
            </div>
          }
        />
      )}
      {referralMaterialModal.data && (
        <Dialog
          open={referralMaterialModal.visible}
          onOpenChange={(open) => !open && handleReferralMaterialModalClose()}
        >
          <ReferralMaterialsForm
            data={referralMaterialModal.data}
            languages={languages.filter((l) => l.clientAreaVisible)}
            tickmillCompanies={tickmillCompanies}
            referralMaterialsTypes={referralMaterialsTypes}
            referralMaterialsCategoriesByEntity={referralMaterialsCategoriesByEntity}
            fetchReferralMaterialsCategoriesByEntity={fetchReferralMaterialsCategoriesByEntity}
            onSubmit={handleSubmit}
          />
        </Dialog>
      )}
      <Breadcrumbs crumbs={crumbs} />
      <SelectFiltersFacade
        gridState={gridState}
        gridStatusActions={gridStatusActions}
        gridStatusState={gridStatusState}
        setGridFilters={gridActions.setGridFilters}
        filterFetch={filterFetch}
        filterType={FilterType.ReferralMaterials}
        filterFields={getReferralMaterialsPageFilterFields()}
        getQuery={getQuery}
      />
      <AddButton text='Add Referral Material' onClick={handleReferralMaterialModalOpen} />

      <TableStateContext.Provider value={[tableData, setTableData]}>
        <GridResizeContext.Provider value={columnResizeCount}>
          <Grid
            {...gridState}
            resizable
            data={referralMaterials}
            sort={gridState.sort}
            total={gridState.total}
            take={gridState.take}
            skip={gridState.skip}
            onSortChange={handleSortChange}
            onColumnResize={handleColumnResizeTracking}
            pageable
            pager={() => (
              <Pager
                onSkipCountChange={handleSkipCountChange}
                onPageChange={handleGridPageChange}
                onPageSizeChange={handlePageSizeChange}
                page={gridState.pageIndex}
                pageSize={gridState.pageSize}
                take={gridState.take || 10}
                skip={gridState.skip || 0}
              />
            )}
            style={{ zIndex: 0 }}
          >
            {referralMaterialsColumn.filter(filterColumn).map((column, idx) => (
              <GridColumn key={idx + 2} {...column} />
            ))}
            <GridColumn
              width={50}
              title=''
              cell={(gridCellProps: GridCellProps) => (
                <td>
                  <MoreButton
                    items={[
                      {
                        name: 'Delete',
                        onClick: () => handleConfirmationModalOpen(gridCellProps.dataItem),
                      },
                    ]}
                  />
                </td>
              )}
            />
          </Grid>
        </GridResizeContext.Provider>
      </TableStateContext.Provider>
    </React.Fragment>
  )
}

const filterColumn = (c: GridColumnProps) => {
  return c.show
}

export default ReferralMaterialsPage
